import { Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './lib/i18n';
import { lazy, Suspense } from 'react';
import { Loader } from './components/ui';

// Lazy-load all routes for performance
const PublicLayout = lazy(() => import('./layouts/PublicLayout'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const SupplierLayout = lazy(() => import('./layouts/SupplierLayout'));

const Home = lazy(() => import('./pages/public/Home'));
const HotelsPage = lazy(() => import('./pages/public/Hotels'));
const HotelDetails = lazy(() => import('./pages/public/HotelDetails'));
const Destinations = lazy(() => import('./pages/public/Destinations'));
const DestinationDetails = lazy(() => import('./pages/public/DestinationDetails'));
const ToursPage = lazy(() => import('./pages/public/Tours'));
const TourDetails = lazy(() => import('./pages/public/TourDetails'));
const PackagesPage = lazy(() => import('./pages/public/Packages'));
const PackageDetails = lazy(() => import('./pages/public/PackageDetails'));
const FlightsPage = lazy(() => import('./pages/public/Flights'));
const CarsPage = lazy(() => import('./pages/public/Cars'));
const TripPlanner = lazy(() => import('./pages/public/TripPlanner'));
const AIAssistant = lazy(() => import('./pages/public/AIAssistant'));
const Login = lazy(() => import('./pages/public/Login'));
const Register = lazy(() => import('./pages/public/Register'));
const Profile = lazy(() => import('./pages/public/Profile'));
const MyBookings = lazy(() => import('./pages/public/MyBookings'));
const MyTrips = lazy(() => import('./pages/public/MyTrips'));
const Favorites = lazy(() => import('./pages/public/Favorites'));
const Checkout = lazy(() => import('./pages/public/Checkout'));
const BookingConfirmation = lazy(() => import('./pages/public/BookingConfirmation'));
const Blog = lazy(() => import('./pages/public/Blog'));
const Article = lazy(() => import('./pages/public/Article'));
const About = lazy(() => import('./pages/info/About'));
const Faq = lazy(() => import('./pages/info/Faq'));
const Legal = lazy(() => import('./pages/info/Legal'));
const City = lazy(() => import('./pages/info/City'));
const Landing = lazy(() => import('./pages/landing/Landing'));
const ManagePage = lazy(() => import('./pages/manage/Manage'));
const ReviewQueue = lazy(() => import('./pages/manage/Review'));
const Contact = lazy(() => import('./pages/public/Contact'));
const NotFound = lazy(() => import('./pages/public/NotFound'));

// Admin
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminBookings = lazy(() => import('./pages/admin/Bookings'));
const AdminCustomers = lazy(() => import('./pages/admin/Customers'));
const AdminSuppliers = lazy(() => import('./pages/admin/Suppliers'));
const AdminPayments = lazy(() => import('./pages/admin/Payments'));
const AdminCMS = lazy(() => import('./pages/admin/CMS'));
const AdminBlog = lazy(() => import('./pages/admin/Blog'));
const AdminIntegrations = lazy(() => import('./pages/admin/Integrations'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const AdminReviews = lazy(() => import('./pages/admin/Reviews'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));
const AdminSupport = lazy(() => import('./pages/admin/Support'));

// Supplier
const SupplierDashboard = lazy(() => import('./pages/supplier/Dashboard'));
const SupplierBookings = lazy(() => import('./pages/supplier/Bookings'));
const SupplierSettlements = lazy(() => import('./pages/supplier/Settlements'));
const SupplierProfile = lazy(() => import('./pages/supplier/Profile'));
const SupplierApply = lazy(() => import('./pages/public/SupplierApply'));

export default function App() {
  return (
    <AppProvider>
      <Suspense fallback={<div className="loader-page"><Loader /></div>}>
        <Routes>
          {/* Public site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/destinations/:slug" element={<DestinationDetails />} />
            <Route path="/hotels" element={<HotelsPage />} />
            <Route path="/hotels/:slug" element={<HotelDetails />} />
            <Route path="/tours" element={<ToursPage />} />
            <Route path="/tours/:slug" element={<TourDetails />} />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/packages/:slug" element={<PackageDetails />} />
            <Route path="/flights" element={<FlightsPage />} />
            <Route path="/cars" element={<CarsPage />} />
            <Route path="/trip-planner" element={<TripPlanner />} />
            <Route path="/ai-assistant" element={<AIAssistant />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/bookings" element={<MyBookings />} />
            <Route path="/bookings/:id" element={<MyBookings />} />
            <Route path="/trips" element={<MyTrips />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/checkout/:orderId" element={<Checkout />} />
            <Route path="/confirmation/:orderId" element={<BookingConfirmation />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<Article />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/cities/:slug" element={<City />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/supplier-apply" element={<SupplierApply />} />
          </Route>

          {/* Auth (no layout) */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="suppliers" element={<AdminSuppliers />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="cms" element={<AdminCMS />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="integrations" element={<AdminIntegrations />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="content" element={<Navigate to="/admin/content/hotels" replace />} />
            <Route path="content/:res" element={<ManagePage />} />
            <Route path="review" element={<ReviewQueue />} />
            <Route path="support" element={<AdminSupport />} />
          </Route>

          {/* Supplier portal */}
          <Route path="/supplier" element={<SupplierLayout />}>
            <Route index element={<SupplierDashboard />} />
            <Route path="bookings" element={<SupplierBookings />} />
            <Route path="settlements" element={<SupplierSettlements />} />
            <Route path="content" element={<Navigate to="/supplier/content/hotels" replace />} />
            <Route path="content/:res" element={<ManagePage />} />
            <Route path="profile" element={<SupplierProfile />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppProvider>
  );
}
